import type { MaybeRefOrGetter, Ref } from "vue";

import {
	type QueryClient,
	useQuery,
} from "@tanstack/vue-query";

import { client } from "@/api";
import { excludedBrokerages } from "@/composables/filters";
import { useDateStore } from "@/stores/dates";

export const invalidateDashboardQueryKeys = (qc: QueryClient) => {
	const keys = [
		"dashboard-stats",
		"dashboard-underwriter-stats",
		"dashboard-wilbur-decision-stats",
		"dashboard-brokerage-stats",
		"dashboard-brokerage-overall-stats",
		"dashboard-flows",
		"dashboard-premium-flows",
		"dashboard-quoted-premium",
		"dashboard-feed",
		"dashboard-market-breakdown",
		"dashboard-pending-reviews",
		"dashboard-flight-volume",
		"dashboard-token-usage",
	];
	for (const key of keys) {
		qc.invalidateQueries({ queryKey: [key] });
	}
};

export const useDashboardStats = () => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.dashboard._stats.get();
		if (resp.status === 200) {
			return resp.data?.stats ?? {
				pendingQuoteCountByUnderwriter: [],
				submissionsTimeseries: [],
				lossReasons: [],
			};
		}
		return {
			pendingQuoteCountByUnderwriter: [],
			submissionsTimeseries: [],
			lossReasons: [],
		};
	},
	queryKey: ["dashboard-stats"],
	refetchOnMount: true,
});

const defaultPremiumFlows = {
	quotedByDay: [],
	boundByDay: [],
	notBoundByDay: [],
};

const defaultFlows = {
	createdByDay: [],
	...defaultPremiumFlows,
};

const flowsRange = () => {
	const until = new Date();
	const since = new Date(until.getTime() - 1000 * 60 * 60 * 24 * 365);
	return {
		since: since.toISOString(),
		until: until.toISOString(),
	};
};

export const useDashboardFlows = () => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.dashboard._flows.get({
			query: flowsRange(),
		});
		if (resp.status === 200) {
			return resp.data?.flows ?? defaultFlows;
		}
		return defaultFlows;
	},
	queryKey: ["dashboard-flows"],
	refetchOnMount: true,
});

export const useDashboardPremiumFlows = (enabled: MaybeRefOrGetter<boolean>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.dashboard._premium_flows.get({
			query: flowsRange(),
		});
		if (resp.status === 200) {
			return resp.data?.flows ?? defaultPremiumFlows;
		}
		return defaultPremiumFlows;
	},
	queryKey: ["dashboard-premium-flows"],
	enabled,
	refetchOnMount: true,
});

export const useDashboardUnderwriterStats = () => useQuery({
	queryFn: async () => {
		const store = useDateStore();
		const since = store.since.toISOString();
		const until = store.until.toISOString();
		const resp = await client.api.v1.dashboard._underwriter_stats.get({
			query: {
				since,
				until,
			},
		});
		if (resp.status === 200) {
			return resp.data?.underwriterStats ?? [];
		}
		return [];
	},
	queryKey: ["dashboard-underwriter-stats"],
	refetchOnMount: true,
});

export const useDashboardWilburDecisionStats = () => useQuery({
	queryFn: async () => {
		const store = useDateStore();
		const response = await client.api.v1.dashboard._wilbur_decision_stats.get({
			query: {
				since: store.since.toISOString(),
				until: store.until.toISOString(),
			},
		});
		return response.data?.wilburDecisionStats ?? {
			decisionCount: 0,
			quoteCount: 0,
			declineCount: 0,
			reviewedCount: 0,
			agreeCount: 0,
			quoteRate: 0,
			declineRate: 0,
			accuracy: null,
			accuracyByDay: [],
		};
	},
	queryKey: ["dashboard-wilbur-decision-stats"],
	refetchOnMount: true,
});

export const useDashboardBrokerageStats = () => useQuery({
	queryFn: async () => {
		const store = useDateStore();
		const since = store.since.toISOString();
		const until = store.until.toISOString();
		const resp = await client.api.v1.dashboard._brokerage_stats.get({
			query: {
				since,
				until,
			},
		});
		if (resp.status === 200) {
			return resp.data?.brokerageStats ?? [];
		}
		return [];
	},
	queryKey: ["dashboard-brokerage-stats"],
	refetchOnMount: true,
});

export const useDashboardBrokerageOverallStats = () => useQuery({
	queryFn: async () => {
		const store = useDateStore();
		const since = store.since.toISOString();
		const until = store.until.toISOString();
		const resp = await client.api.v1.dashboard._brokerage_stats_overall.get({
			query: {
				since,
				until,
				excludeBrokerages: excludedBrokerages.value,
			},
		});
		return resp.data?.overallStats ?? {
			submissionCount: 0,
			quotedCount: 0,
			boundCount: 0,
			notBoundCount: 0,
			quoteToSubmissionRatio: 0,
			bindToQuoteRatio: 0,
			quoteToNotBoundRatio: 0,
		};
	},
	queryKey: ["dashboard-brokerage-overall-stats"],
	refetchOnMount: true,
});

export const useDashboardQuotedPremium = () => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.dashboard._quoted_premium.get();
		if (resp.status === 200) {
			return resp.data?.quotedPremium ?? {
				premium: 0, winRate: 0, projectedPremium: 0, lagDays: 0,
			};
		}
		return {
			premium: 0, winRate: 0, projectedPremium: 0, lagDays: 0,
		};
	},
	queryKey: ["dashboard-quoted-premium"],
	refetchOnMount: true,
});

export const useDashboardPendingReviews = () => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.dashboard._pending_reviews.get();
		if (resp.status === 200) {
			return resp.data?.pendingReviews ?? [];
		}
		return [];
	},
	queryKey: ["dashboard-pending-reviews"],
	refetchOnMount: true,
});

export const useDashboardFeed = () => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.dashboard._feed.get();
		if (resp.status === 200) {
			return resp.data?.activity ?? [];
		}
		return [];
	},
	queryKey: ["dashboard-feed"],
	refetchOnMount: true,
});

export const useDashboardMarketBreakdown = () => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.dashboard._market_breakdown.get();
		if (resp.status === 200) {
			return resp.data?.breakdown ?? [];
		}
		return [];
	},
	queryKey: ["dashboard-market-breakdown"],
	refetchOnMount: true,
});

const defaultFlightVolume = {
	flights: [],
	plans: [],
};

export const useDashboardFlightVolume = (days: Ref<number>) => useQuery({
	queryFn: async () => {
		const until = new Date();
		const since = new Date(until.getTime() - (days.value - 1) * 24 * 60 * 60 * 1000);
		const resp = await client.api.v1.dashboard._flight_volume.get({
			query: {
				since: since.toISOString(),
				until: until.toISOString(),
			},
		});
		if (resp.status === 200) {
			return resp.data?.flightVolume ?? defaultFlightVolume;
		}
		return defaultFlightVolume;
	},
	queryKey: ["dashboard-flight-volume", days],
	staleTime: 60_000,
});

export type TokenUsageFilters = {
	days: number;
	groupBy: "feature" | "model";
	feature?: string;
	model?: string;
};

const defaultTokenUsage = {
	series: [],
	totals: [],
	features: [],
	models: [],
};

export const useDashboardTokenUsage = (filters: Ref<TokenUsageFilters>) => useQuery({
	queryFn: async () => {
		const { days, ...query } = filters.value;
		const until = new Date();
		const since = new Date(until.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
		const resp = await client.api.v1.dashboard._token_usage.get({
			query: {
				since: since.toISOString(),
				until: until.toISOString(),
				...query,
			},
		});
		if (resp.status === 200) {
			return resp.data?.tokenUsage ?? defaultTokenUsage;
		}
		return defaultTokenUsage;
	},
	queryKey: ["dashboard-token-usage", filters],
	staleTime: 60_000,
});
